import { GroupBase, StylesConfig } from 'react-select';
import {
  Country,
  MENU_DRAWER_WIDTH
} from '~/App/shared/localization/components/constants';
import { theme } from '@kvdbil/components';
import { CSSObject } from 'styled-components';

// Shared so the desktop control and its dropdown line up at the same width.
const LANGUAGE_SELECT_WIDTH = '9.625rem'; // 154px
const LANGUAGE_SELECT_COMPACT_WIDTH = '4rem'; // 64px

const selectedOptionStyle = (isSelected: boolean, isFocused: boolean) => {
  const selectedColor = theme.colors.text.light;
  const focusedColor = theme.colors.primary.main;
  const selectedBackground = theme.colors.info.main;
  const focusedBackground = theme.colors.background.gray;

  const getBackgroundColor = () => {
    if (isSelected) return selectedBackground;
    if (isFocused) return focusedBackground;
    return 'none';
  };

  const getTextColor = () => {
    if (isSelected) return selectedColor;
    if (isFocused) return focusedColor;
    return theme.colors.text.dark;
  };

  return {
    color: getTextColor(),
    background: getBackgroundColor(),
    '&:hover': {
      background: isSelected ? selectedBackground : focusedBackground,
      color: getTextColor()
    }
  };
};

// Shared between the header and modal select styles so the group heading
// (e.g. "Within Sweden" / "Outside Sweden") stays consistent across both.
const groupHeadingStyle = (
  paddingLeft: string,
  fontSize: string,
  fontWeight: number
): CSSObject => ({
  paddingLeft,
  fontFamily: theme.typography.fontBaseFamily,
  fontSize,
  fontWeight,
  textTransform: 'none',
  color: theme.colors.text.dark
});

export type LanguageSelectVariant = 'desktopHeader' | 'menuHeader';

export const getLanguageSelectStyles = (
  variant: LanguageSelectVariant,
  focusByKeyboard: boolean,
  isMobile: boolean
): StylesConfig<Country, false, GroupBase<Country>> => {
  const isDesktopHeader = variant === 'desktopHeader';

  return {
    control: (styles, { isFocused }) => ({
      ...styles,
      ...({
        display: 'flex',
        gap: '0.5rem',
        padding: isDesktopHeader ? '0 1rem' : '0',
        marginLeft: isDesktopHeader ? '0' : '1rem',
        width: isDesktopHeader
          ? LANGUAGE_SELECT_WIDTH
          : LANGUAGE_SELECT_COMPACT_WIDTH,
        backgroundColor: 'none',
        border: 'none',
        borderRadius: 0,
        color: theme.colors.text.dark,
        lineHeight: '1.5rem',
        fontFamily: theme.typography.fontBaseFamily,
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: 'none',
        minHeight: '40px',
        '&:hover': {
          backgroundColor: isDesktopHeader
            ? theme.colors.background.gray
            : 'transparent'
        },
        '& svg': {
          width: isDesktopHeader ? '1rem' : '1.5rem',
          height: isDesktopHeader ? '1rem' : '1.5rem',
          color: theme.colors.grayscaleToned.dark1
        },
        outline: isFocused && focusByKeyboard ? '1px auto blue' : 'none'
      } as CSSObject)
    }),
    valueContainer: styles => ({ ...styles, ...({ padding: 0 } as CSSObject) }),
    indicatorsContainer: styles => ({
      ...styles,
      paddingRight: 0,
      // Indicator is a div element, it will get weird positioning without this
      ...({
        '& div': {
          display: 'flex',
          justifyItems: 'center',
          alignItems: 'center',
          padding: 0
        }
      } as CSSObject)
    }),
    singleValue: styles => ({
      ...styles,
      ...({
        display: 'flex',
        flexDirection: 'row',
        gap: '1rem',
        margin: 0,
        alignItems: 'center',
        justifyContent: 'start',
        color: theme.colors.text.dark,
        fontFamily: theme.typography.fontBaseFamily,
        '& svg': {
          width: isDesktopHeader ? '1rem' : '1.5rem',
          height: isDesktopHeader ? '1rem' : '1.5rem'
        },
        '& span:last-child': {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }
      } as CSSObject)
    }),
    input: styles => ({
      ...styles,
      ...({ height: 'auto', margin: 0, padding: 0 } as CSSObject)
    }),
    menu: styles => ({
      ...styles,
      ...({
        top: isDesktopHeader ? 'auto' : '100%',
        left: isDesktopHeader ? 'auto' : '-2rem',
        width: isDesktopHeader
          ? LANGUAGE_SELECT_WIDTH
          : isMobile
            ? '100vw'
            : MENU_DRAWER_WIDTH,
        backgroundColor: theme.colors.background.light,
        borderRadius: 0,
        // Divider line marks the end of the expanded language list
        boxShadow: isDesktopHeader ? 'none' : `${theme.elevations.elevation8}`,
        marginTop: isDesktopHeader ? '-0.1rem' : '0.5rem',
        zIndex: 10000,
        '& div:first-child': {
          maxHeight: isDesktopHeader ? '20.625rem' : '24.5rem'
        }
      } as CSSObject)
    }),
    menuList: styles => ({
      ...styles,
      ...({
        gap: '0',
        padding: '0',
        display: 'flex',
        flexDirection: 'column'
      } as CSSObject)
    }),
    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      ...({
        padding: isDesktopHeader ? '0.5rem 1rem' : '0.5rem 2rem',
        gap: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        fontFamily: theme.typography.fontBaseFamily,
        fontSize: isDesktopHeader ? '0.75rem' : '1rem',
        fontWeight: isDesktopHeader ? 600 : 400,
        minHeight: isDesktopHeader ? 'auto' : '40px',
        cursor: 'pointer',
        ...selectedOptionStyle(isSelected, isFocused),
        '& svg': {
          width: isDesktopHeader ? '1rem' : '1.5rem',
          height: isDesktopHeader ? '1rem' : '1.5rem'
        },
        '& span:last-child': {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }
      } as CSSObject)
    }),
    group: styles => ({
      ...styles,
      ...({
        padding: '0.25rem 0'
      } as CSSObject)
    }),
    groupHeading: styles => ({
      ...styles,
      ...groupHeadingStyle(
        isDesktopHeader ? '1rem' : '2rem',
        isDesktopHeader ? '0.75rem' : '1rem',
        isDesktopHeader ? 600 : 400
      )
    })
  };
};

export const languageSelectModalStyles: StylesConfig<
  Country,
  false,
  GroupBase<Country>
> = {
  control: styles => ({
    ...styles,
    ...({
      width: '100%',
      border: `2px solid ${theme.colors.text.dark}`,
      ':hover': {
        borderColor: theme.colors.text.dark
      },
      borderRadius: 0,
      backgroundColor: 'none',
      boxShadow: 'none',
      minHeight: '60px'
    } as CSSObject)
  }),
  valueContainer: styles => ({
    ...styles,
    ...({ paddingLeft: '1.5rem' } as CSSObject)
  }),
  indicatorsContainer: styles => ({
    ...styles,
    ...({
      '& svg': {
        width: '1.5rem',
        height: '1.5rem',
        color: theme.colors.grayscaleToned.dark1
      }
    } as CSSObject)
  }),
  placeholder: styles => ({
    ...styles,
    ...({ color: theme.colors.text.dark } as CSSObject)
  }),
  singleValue: styles => ({
    ...styles,
    ...({
      display: 'flex',
      gap: '1rem',
      margin: 0,
      alignItems: 'center'
    } as CSSObject)
  }),
  menu: styles => ({
    ...styles,
    ...({
      backgroundColor: theme.colors.background.light,
      border: `2px solid ${theme.colors.text.dark}`,
      borderRadius: 0,
      margin: '-2px 0',
      boxShadow: 'none'
    } as CSSObject)
  }),
  menuList: styles => ({
    ...styles,
    ...({
      padding: 0
    } as CSSObject)
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    ...({
      padding: '1rem 1.5rem',
      gap: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      cursor: 'pointer',
      ...selectedOptionStyle(isSelected, isFocused)
    } as CSSObject)
  }),
  menuPortal: styles => ({ ...styles, zIndex: 9999 }),
  groupHeading: styles => ({
    ...styles,
    ...groupHeadingStyle('1.5rem', '1rem', 400)
  })
};
